import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, Sparkles, ArrowRight, ExternalLink, Check, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errorMessage } from "@/lib/api";
import { useDeliveryUpdater, useSession } from "@/lib/queries";
import { CHANGE_STATUS_LABELS, SPEC_STATUS_LABELS, PRODUCTION_STATUS_LABELS, INSTALL_STATUS_LABELS, weekLabel, formatDate, formatDateTime, formatAmount, addressLine } from "@/lib/format";
import { messageHeadline } from "@/lib/followup";
import { WorkSurface, PrereqList, KVList, ActionLink } from "@/components/common/Blocks";
import { AiSource } from "@/components/common/AiPanel";
import { StateChip, toneForChangeStatus } from "@/components/common/Badges";
import { DrawingFrame } from "@/components/common/Viewers";

const noSpecPhase = (d) => ["foresporsel", "tilbud"].includes(d.phase);

/* ------------------------------------------------------------------ Quote */
const Quote = ({ m }) => (
  <blockquote className="rounded-md border-l-2 surface-subtle px-4 py-3" data-testid="source-message-body" style={{ borderLeftColor: "hsl(var(--foreground) / 0.35)" }}>
    <p className="whitespace-pre-line text-[15px] leading-relaxed">{m.body}</p>
    <footer className="t-meta mt-2">{m.sender} · {m.channel} · {formatDateTime(m.received_at)}{m.subject ? ` · «${m.subject}»` : ""}</footer>
  </blockquote>
);

/* ------------------------------------------------------------------ Handling a customer message */
/**
 * One work surface for an unhandled customer message. Used from the delivery page and as
 * step 1 of the change flow, so the user learns one way of doing the job.
 */
export const MessageWorkSurface = ({ d, messageId, onRegistered, onAddMessage }) => {
  const navigate = useNavigate();
  const update = useDeliveryUpdater();
  const { data: session } = useSession();
  const aiOn = session?.ai?.configured;
  const inMsgs = d.messages.filter((m) => m.direction === "inn").sort((a, b) => b.received_at.localeCompare(a.received_at));
  const unhandled = inMsgs.filter((m) => !m.handled);
  const [msgId, setMsgId] = useState(messageId || unhandled[0]?.id || inMsgs[0]?.id || "");
  const msg = inMsgs.find((m) => m.id === msgId);
  const rev = d.computed.current_spec_revision || d.computed.pending_spec_revision;
  const fields = rev?.fields || [];
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [clarify, setClarify] = useState([]);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualField, setManualField] = useState("");
  const [manualTo, setManualTo] = useState("");
  const [showManual, setShowManual] = useState(false);

  const headline = msg ? messageHeadline(msg) : "Endring uten kundebeskjed";
  const simple = noSpecPhase(d);
  const eyebrow = msg ? "Kundebeskjed · ikke vurdert" : "Manuell registrering";
  const meta = msg ? `${msg.sender} · mottatt ${formatDateTime(msg.received_at)}` : "Endring som ikke kommer fra en registrert kundebeskjed";

  const runAi = async () => {
    setBusy(true);
    setAiError(null);
    try {
      const { data } = await api.post("/ai/assess-message", { delivery_id: d.id, message_id: msg?.id });
      const s = data.suggestion;
      setTitle((t) => t || s.tittel || headline);
      setItems((s.endringsforslag || []).map((e) => {
        const f = fields.find((x) => x.key === e.field_key) || fields.find((x) => x.label.toLowerCase() === (e.felt || "").toLowerCase());
        return { include: true, field_key: f?.key || null, label: f?.label || e.felt, from: f?.value ?? e.fra ?? null, to: e.til || "", source: e.kilde, origin: "ki" };
      }));
      setQuestions((s.sporsmal_fra_kunde || []).map((q) => ({ include: true, question: q.sporsmal, source: q.kilde })));
      setClarify([...(s.uavklart || []), ...(s.bor_vurderes || [])]);
      setAiUsed(true);
    } catch (e) {
      const m = errorMessage(e, "KI-forslaget feilet. Legg inn endringspunktene manuelt.");
      setAiError(m);
    } finally {
      setBusy(false);
    }
  };

  const addManual = () => {
    const f = fields.find((x) => x.key === manualField);
    if (!f || !manualTo.trim()) return;
    setItems([...items, { include: true, field_key: f.key, label: f.label, from: f.value, to: manualTo.trim(), source: null, origin: "manuell" }]);
    setManualField("");
    setManualTo("");
    if (!title) setTitle(headline);
  };

  const markHandled = async () => {
    if (!msg) return;
    try {
      const { data } = await api.post(`/deliveries/${d.id}/messages/${msg.id}/handled`, { actor: d.responsible });
      update(data);
      toast.success("Beskjeden er markert som behandlet", { description: "Ingen endring er registrert." });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const save = async () => {
    const chosen = items.filter((i) => i.include && i.to.trim());
    if (!chosen.length) { toast.error("Bekreft minst ett endringspunkt."); return; }
    const t = (title || headline).trim();
    setSaving(true);
    try {
      const { data } = await api.post(`/deliveries/${d.id}/changes`, {
        title: t,
        source_message_id: msg?.id || null,
        items: chosen.map((i) => ({ field_key: i.field_key, label: i.label, from: i.from, to: i.to.trim(), source_quote: i.source, origin: i.origin })),
        customer_questions: questions.filter((q) => q.include).map((q) => ({ question: q.question, source_quote: q.source })),
        ai_assisted: aiUsed,
      });
      update(data);
      const cr = data.change_requests[data.change_requests.length - 1];
      toast.success(`Endring ${cr.number} registrert`, { description: "Neste steg: vurder konsekvensene manuelt." });
      if (onRegistered) onRegistered(cr); else navigate(`/leveranser/${d.id}/endring/${cr.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const picker = unhandled.length > 1 && (
    <Select value={msgId} onValueChange={setMsgId}>
      <SelectTrigger className="h-9 w-[220px]" data-testid="source-message-select"><SelectValue /></SelectTrigger>
      <SelectContent>{inMsgs.map((m) => <SelectItem key={m.id} value={m.id}>{formatDate(m.received_at)} · {messageHeadline(m)}</SelectItem>)}</SelectContent>
    </Select>
  );

  const more = msg && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Flere valg" data-testid="message-more"><MoreHorizontal /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {onAddMessage && <DropdownMenuItem onClick={onAddMessage} data-testid="message-reply">Registrer svar eller ny melding</DropdownMenuItem>}
        <DropdownMenuItem onClick={markHandled} data-testid="message-mark-handled">Marker som behandlet uten endring</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /* Inquiry / quote phase: there is no specification to change yet. */
  if (simple && msg) {
    return (
      <WorkSurface eyebrow={eyebrow} title={headline} meta={meta} actions={<>{picker}{more}</>} testId="message-work-surface"
        footer={<>
          <p className="t-meta">Henvendelsen avklares med kunden før tilbud. Spesifikasjon opprettes når bestilling foreligger.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={markHandled} data-testid="message-handled-button">Marker som behandlet</Button>
            {onAddMessage && <Button onClick={onAddMessage} data-testid="message-reply-button">Registrer svar</Button>}
          </div>
        </>}>
        <Quote m={msg} />
      </WorkSurface>
    );
  }

  const hasProposal = items.length > 0 || aiUsed;

  return (
    <WorkSurface eyebrow={eyebrow} title={headline} meta={meta} actions={<>{picker}{more}</>} testId="message-work-surface"
      footer={<>
        <p className="t-meta">Registrering endrer ikke spesifikasjonen. Pris, leveringsuke og tegning vurderes manuelt i neste steg.</p>
        <div className="flex flex-wrap gap-2">
          {msg && <Button variant="ghost" onClick={markHandled} data-testid="message-handled-button">Behandlet uten endring</Button>}
          <Button onClick={save} disabled={saving || !items.some((i) => i.include && i.to.trim())} data-testid="change-register-confirm-button">{saving && <Loader2 className="animate-spin" />} Registrer endring <ArrowRight /></Button>
        </div>
      </>}>
      <div className="space-y-6">
        {msg && <Quote m={msg} />}

        <div data-testid="register-form">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-[15.5px] font-semibold">Forslag til endring</h3>
              <p className="t-meta mt-0.5">Gjeldende verdi fra spesifikasjon rev. {rev?.code || "–"} og det kunden ønsker. Du bekrefter hva som registreres.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {msg && <Button variant={hasProposal ? "outline" : "default"} size="sm" className="h-9" onClick={runAi} disabled={busy || !aiOn} data-testid="ai-assess-button">
                {busy ? <Loader2 className="animate-spin" /> : <Sparkles />} {busy ? "Leser beskjeden…" : aiUsed ? "Hent forslag på nytt" : "Foreslå endring med KI"}
              </Button>}
              <Button variant="outline" size="sm" className="h-9" onClick={() => setShowManual((v) => !v)} data-testid="manual-toggle-button"><Plus /> Legg til punkt</Button>
            </div>
          </div>
          {!aiOn && <p className="t-meta mt-2">KI er ikke konfigurert i dette miljøet – legg inn endringspunkter manuelt.</p>}
          {aiError && <p className="mt-2 text-[14px]" style={{ color: "hsl(var(--state-rest))" }} data-testid="ai-error">{aiError}</p>}
          {aiUsed && (
            <p className="ai-label mt-3" data-testid="ai-suggestion-label"><Sparkles className="h-3.5 w-3.5" /> Forslag fra KI, lest ut av beskjeden og sammenlignet med rev. {rev?.code}. Alle punkter kan redigeres eller fjernes. KI setter ikke pris eller leveringsløfte.</p>
          )}

          <ul className="mt-3 divide-y border-y" data-testid="change-items">
            {items.map((it, i) => (
              <li key={i} className={`grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 py-3 ${it.include ? "" : "opacity-60"}`} data-testid={`change-item-${i}`}>
                <Checkbox checked={it.include} onCheckedChange={(v) => setItems(items.map((x, j) => (j === i ? { ...x, include: !!v } : x)))} className="mt-1" data-testid={`change-item-include-${i}`} aria-label="Ta med" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-[15px] font-medium">{it.label}</span>
                    {!it.field_key && <StateChip tone="rest" strong>Matcher ikke et felt i spesifikasjonen</StateChip>}
                  </div>
                  <div className="mt-2 grid gap-x-4 gap-y-2 sm:grid-cols-2">
                    <div>
                      <p className="t-label">Gjeldende · rev. {rev?.code}</p>
                      <p className="mt-1 flex min-h-[40px] items-center rounded-md border border-dashed px-3 text-[14.5px] text-muted-foreground">{it.from || "–"}</p>
                    </div>
                    <div>
                      <Label className="t-label" htmlFor={`to-${i}`}>Kundens ønske</Label>
                      <Input id={`to-${i}`} value={it.to} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))} className="mt-1 h-10 text-[14.5px] font-medium" data-testid={`change-item-to-${i}`} />
                    </div>
                  </div>
                  <AiSource quote={it.source} />
                </div>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Fjern" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 /></Button>
              </li>
            ))}
            {!items.length && (
              <li className="py-5 text-[14.5px] text-muted-foreground" data-testid="no-items">
                Ingen endringspunkter ennå. {aiOn && msg ? "Be om forslag fra KI eller legg til et punkt manuelt." : "Legg til et punkt manuelt."}
              </li>
            )}
          </ul>

          {(showManual || !msg || (!aiOn && !items.length)) && (
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]" data-testid="manual-item-form">
              <Select value={manualField} onValueChange={setManualField}>
                <SelectTrigger data-testid="manual-field-select" className="h-10"><SelectValue placeholder="Felt i spesifikasjonen" /></SelectTrigger>
                <SelectContent>{fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label} · {f.value}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={manualTo} onChange={(e) => setManualTo(e.target.value)} placeholder="Ny verdi (kundens ønske)" className="h-10" data-testid="manual-to-input" />
              <Button variant="outline" onClick={addManual} disabled={!manualField || !manualTo.trim()} data-testid="manual-add-button"><Plus /> Legg til</Button>
            </div>
          )}
        </div>

        {(clarify.length > 0 || questions.length > 0) && (
          <div data-testid="clarify-list">
            <h3 className="text-[15.5px] font-semibold">Må avklares</h3>
            <p className="t-meta mt-0.5">Ikke svar – punkter ansvarlig må ta stilling til i vurderingen.</p>
            <ul className="mt-2 space-y-1.5">
              {clarify.map((c, i) => (
                <li key={`c${i}`} className="flex items-start gap-2.5 text-[14.5px]"><span className="dot mt-[8px]" style={{ background: "hsl(var(--state-waiting))" }} aria-hidden="true" /><span>{c}</span></li>
              ))}
              {questions.map((q, i) => (
                <li key={`q${i}`}>
                  <label className="flex items-start gap-2.5 text-[14.5px]" data-testid={`customer-question-${i}`}>
                    <Checkbox checked={q.include} onCheckedChange={(v) => setQuestions(questions.map((x, j) => (j === i ? { ...x, include: !!v } : x)))} className="mt-0.5" />
                    <span><span className="t-label mr-1.5">Kunden spør</span>{q.question}<AiSource quote={q.source} /></span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasProposal && (
          <div className="grid gap-1.5 sm:max-w-md">
            <Label htmlFor="ch-title" className="text-[14px] font-medium">Tittel på endringen</Label>
            <Input id="ch-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={headline} className="h-10" data-testid="change-title-input" />
          </div>
        )}
      </div>
    </WorkSurface>
  );
};

/* ------------------------------------------------------------------ Open change request */
const STEPS = ["Registrert", "Vurdert", "Grunnlag oppdatert", "Kundegodkjent", "Frigitt"];
const stepIndex = (s) => ({ registrert: 0, vurdert: 1, grunnlag_oppdatert: 2, til_kundegodkjenning: 2, kundegodkjent: 3, endring_onsket: 1, frigitt: 4, avvist: 4 }[s] ?? 0);

const NEXT = {
  registrert: { label: "Vurder konsekvenser", step: null },
  vurdert: { label: "Oppdater grunnlag", step: null },
  grunnlag_oppdatert: { label: "Send til kundegodkjenning", step: null },
  endring_onsket: { label: "Vurder på nytt", step: null },
  kundegodkjent: { label: "Start intern kontroll", step: null },
};

export const ChangeSurface = ({ d, cr, onRelease }) => {
  const ar = d.approval_requests.find((a) => a.id === cr.approval_request_id);
  const idx = stepIndex(cr.status);
  const next = NEXT[cr.status];
  const a = cr.assessment;
  const src = d.messages.find((m) => m.id === cr.source_message_id);
  const href = `/leveranser/${d.id}/endring/${cr.id}`;

  const primary = (() => {
    if (cr.status === "til_kundegodkjenning" && ar) {
      return <Button asChild variant="outline"><Link to={`/kunde/${ar.token}`} target="_blank" data-testid="open-customer-view-link"><ExternalLink /> Åpne kundevisning (simulert)</Link></Button>;
    }
    if (cr.status === "kundegodkjent") return <Button onClick={onRelease} data-testid="change-release-button">Start intern kontroll <ArrowRight /></Button>;
    if (next) return <Button asChild><Link to={href} data-testid="change-continue-button">{next.label} <ArrowRight /></Link></Button>;
    return null;
  })();

  return (
    <WorkSurface eyebrow={`Endring ${cr.number} · ${CHANGE_STATUS_LABELS[cr.status]}`} title={cr.title}
      meta={`Registrert ${formatDateTime(cr.created_at)} av ${cr.created_by}${src ? ` · fra kundebeskjed ${formatDate(src.received_at)}` : ""}${cr.ai_assisted ? " · KI-forslag kontrollert" : ""}`}
      actions={<>{primary}<Button asChild variant="ghost" size="icon" className="h-9 w-9" aria-label="Åpne endringen"><Link to={href} data-testid="open-change-detail"><MoreHorizontal /></Link></Button></>} testId="change-work-surface">
      <div className="space-y-5">
        <ol className="flex flex-wrap gap-x-5 gap-y-1" data-testid="change-progress">
          {STEPS.map((s, i) => (
            <li key={s} className={`inline-flex items-center gap-1.5 text-[13.5px] ${i < idx || cr.status === "frigitt" ? "text-foreground" : i === idx ? "font-semibold text-foreground" : "text-muted-foreground"}`} aria-current={i === idx ? "step" : undefined}>
              {i < idx || cr.status === "frigitt" ? <Check className="h-3.5 w-3.5" style={{ color: "hsl(var(--state-released))" }} /> : <span className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-primary" : "bg-border"}`} />}
              {s}
            </li>
          ))}
        </ol>

        <dl className="divide-y border-y text-[14.5px]" data-testid="change-items-summary">
          {cr.items.map((i) => (
            <div key={i.id} className="grid grid-cols-[minmax(110px,32%)_1fr] gap-3 py-2.5" data-testid={`summary-item-${i.id}`}>
              <dt className="t-meta pt-0.5">{i.label}</dt>
              <dd className="flex flex-wrap items-center gap-2"><span className="diff-from">{i.from || "–"}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="diff-to">{i.to}</span></dd>
            </div>
          ))}
        </dl>

        {cr.status === "til_kundegodkjenning" && ar && (
          <p className="text-[14.5px]" data-testid="waiting-approval-card"><StateChip tone="waiting" strong>Venter på kundegodkjenning</StateChip> <span className="t-meta">Sendt {formatDateTime(ar.sent_at)} til {ar.recipient}. Ingen e-post sendes i prototypen.</span></p>
        )}
        {cr.status === "endring_onsket" && cr.customer_decision && (
          <p className="text-[14.5px]" data-testid="customer-requested-change-note"><StateChip tone="rest" strong>Kunden ber om endring</StateChip> <span className="text-muted-foreground">«{cr.customer_decision.comment}» · {formatDateTime(cr.customer_decision.at)}</span></p>
        )}
        {cr.status === "kundegodkjent" && cr.customer_decision && (
          <p className="text-[14.5px]" data-testid="approval-decision"><StateChip tone="released" strong>Kundegodkjent</StateChip> <span className="t-meta">{cr.customer_decision.name} · {formatDateTime(cr.customer_decision.at)} · gjelder først etter intern teknisk kontroll.</span></p>
        )}
        {a && (
          <p className="t-meta" data-testid="change-assessment-line">
            Vurdert: pris {a.price === "ingen" ? "uendret" : a.price === "ukjent" ? "må vurderes" : `${a.price} ${formatAmount(a.price_amount)}`.toLowerCase()} · levering {a.delivery === "uendret" ? `${weekLabel(d.dates.agreed_delivery_week).toLowerCase()} holder` : a.delivery === "ny_uke" ? `ny ${weekLabel(a.new_agreed_week).toLowerCase()}` : "må vurderes"} · tegning {a.drawing === "ny_revisjon" ? "ny revisjon" : "uendret"}
          </p>
        )}
      </div>
    </WorkSurface>
  );
};

/* ------------------------------------------------------------------ Initial approval / release */
export const ApprovalWaitingSurface = ({ ar }) => (
  <WorkSurface eyebrow="Tegning og spesifikasjon" title="Venter på kundegodkjenning" meta={`Sendt ${formatDateTime(ar.sent_at)} til ${ar.recipient} · ingen e-post sendes i prototypen`}
    actions={<Button asChild variant="outline"><Link to={`/kunde/${ar.token}`} target="_blank" data-testid="open-customer-view-link"><ExternalLink /> Åpne kundevisning (simulert)</Link></Button>} testId="waiting-approval-card">
    <p className="text-[14.5px] text-muted-foreground">Kunden får en begrenset kundevisning med tegning og spesifikasjon, og svarer godkjenn eller be om endring. Når kunden har svart, vises neste steg her.</p>
  </WorkSurface>
);

export const ReleaseReadySurface = ({ d, rev, onRelease }) => (
  <WorkSurface eyebrow={`Spesifikasjon rev. ${rev.code} · ${SPEC_STATUS_LABELS[rev.status]}`} title="Klar til intern kontroll" meta={rev.customer_approved_at ? `Kundegodkjent ${formatDateTime(rev.customer_approved_at)} av ${rev.customer_approved_by}` : null}
    actions={<Button onClick={onRelease} data-testid="prereq-release-button">Start kontroll <ArrowRight /></Button>} testId="release-ready-surface">
    <p className="text-[14.5px] text-muted-foreground">Kundegodkjenning er ikke intern frigivelse. Grunnlaget kontrolleres teknisk og frigis før det gjelder for produksjon.</p>
  </WorkSurface>
);

export const SendApprovalSurface = ({ rev, onSend }) => (
  <WorkSurface eyebrow={`Spesifikasjon rev. ${rev.code} · utkast`} title="Klar til å sendes kunden" meta="Tegning og spesifikasjon må godkjennes av kunden før intern kontroll"
    actions={<Button onClick={onSend} data-testid="delivery-send-approval-button">Send til kundegodkjenning <ArrowRight /></Button>} testId="send-approval-surface">
    <p className="text-[14.5px] text-muted-foreground">Kunden får en begrenset kundevisning. Ingen e-post sendes i prototypen – du åpner kundevisningen selv.</p>
  </WorkSurface>
);

/** Calm state: nothing to handle right now. One readable line. */
export const CalmSurface = ({ na }) => (
  <div className="rounded-lg border border-dashed px-5 py-5" data-testid="calm-surface">
    <p className="t-label">Aktuell behandling</p>
    <p className="mt-1 text-[15.5px]">{na?.label || "Ingen åpen behandling"}</p>
    <p className="t-meta mt-1">Ingenting krever behandling på denne leveransen nå.</p>
  </div>
);

/* ------------------------------------------------------------------ Current basis (stable right column) */
export const BasisPanel = ({ d, onTab }) => {
  const c = d.computed;
  const cur = c.current_spec_revision;
  const pend = c.pending_spec_revision;
  return (
    <aside className="space-y-7" data-testid="current-basis" aria-label="Gjeldende grunnlag">
      <div>
        <h2 className="h-section border-b pb-2">Gjeldende grunnlag</h2>
        <KVList items={[
          ["Spesifikasjon", cur ? <span data-testid="basis-spec"><span className="font-medium">Rev. {cur.code}</span> <span className="t-meta">· frigitt {formatDate(cur.released_at)}</span></span> : <span className="text-muted-foreground">Ingen frigitt spesifikasjon</span>],
          pend && ["Under behandling", <span data-testid="basis-pending"><span className="font-medium">Rev. {pend.code}</span> <span className="t-meta">· {SPEC_STATUS_LABELS[pend.status]} · gjelder ikke ennå</span></span>],
          ["Tegning", c.current_drawings.length ? c.current_drawings.map((x) => `${x.title} rev. ${x.code}`).join(" · ") : <span className="text-muted-foreground">Ingen godkjent tegning</span>],
          ["Produksjon", `${PRODUCTION_STATUS_LABELS[d.production.status] || d.production.status}${d.production.planned_week ? ` · ${weekLabel(d.production.planned_week).toLowerCase()}` : ""}`],
          ["Montering", `${INSTALL_STATUS_LABELS[d.installation.status] || d.installation.status}${d.installation.planned_week ? ` · ${weekLabel(d.installation.planned_week).toLowerCase()}` : ""}${d.installation.planned_date ? ` · ${formatDate(d.installation.planned_date)}` : ""}${d.installation.installer ? ` · ${d.installation.installer}` : ""}`],
          ["Avtalt levering", d.dates.agreed_delivery_week ? weekLabel(d.dates.agreed_delivery_week) : "Ikke avtalt"],
        ]} />
        {c.current_drawings.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            {c.current_drawings.map((x) => (
              <button type="button" key={x.id} onClick={() => onTab("dokumenter", `d-${x.id}`)} className="group block text-left" data-testid={`basis-drawing-${x.kind}`}>
                <DrawingFrame drawing={x} compact />
                <p className="t-meta mt-1 group-hover:text-foreground">{x.title} · rev. {x.code}</p>
              </button>
            ))}
          </div>
        )}
        <ActionLink onClick={() => onTab("dokumenter")} className="mt-3 !text-[14px]" testId="basis-open-documents">Åpne dokumenter</ActionLink>
      </div>

      <div data-testid="prerequisites-section">
        <div className="flex items-end justify-between border-b pb-2">
          <h2 className="h-section">Forutsetninger for produksjon</h2>
          <StateChip tone={c.production_ready ? "released" : "waiting"} strong>{c.production_ready ? "Klar" : "Ikke klar"}</StateChip>
        </div>
        <PrereqList items={c.prerequisites} />
      </div>

      <div data-testid="roles-section">
        <h2 className="h-section border-b pb-2">Kontakter</h2>
        <KVList items={[
          ["Bestiller", d.roles?.orderer ? `${d.roles.orderer.name}${d.roles.orderer.phone ? ` · ${d.roles.orderer.phone}` : ""}` : "–"],
          ["Byggeplass", d.roles?.site_contact ? `${d.roles.site_contact.name}${d.roles.site_contact.phone ? ` · ${d.roles.site_contact.phone}` : ""}` : "Ikke satt"],
          ["Adresse", addressLine(d.address)],
          ["Ansvarlig", d.responsible],
        ]} />
      </div>
    </aside>
  );
};
